import { readableCandidateText } from './candidateFallback';
import { sanitizeCandidateStylesheet } from './candidateStylesheet';

const MAX_MIRROR_ELEMENTS = 50000;
const MAX_VISIBILITY_CSS_BYTES = 1000000;
const DEFAULT_VIEWPORT_WIDTH = 1024;
const MAX_VIEWPORT_WIDTH = 10000;
const VISIBILITY_PROPERTIES = ['display', 'visibility', 'opacity'];
const NON_CONTENT_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK',
    'META', 'BASE', 'AUDIO', 'VIDEO', 'SOURCE', 'TRACK']);
const SAFE_SELECTOR_ATTRIBUTE = /^(?:id|class|dir|lang|hidden|title|name|type|aria-[\w-]+|data-[\w-]+)$/i;

export interface CandidateVisibleText {
    text: string;
    warning?: string;
}

const visibilityDeclarations = (declarations: string): string => {
    const source = document.createElement('span');
    source.setAttribute('style', declarations);
    const result = document.createElement('span');
    VISIBILITY_PROPERTIES.forEach((property) => {
        const value = source.style.getPropertyValue(property);
        if (value) result.style.setProperty(property, value, source.style.getPropertyPriority(property));
    });
    return result.getAttribute('style') || '';
};

export const visibleCandidateFallback = (
    original: HTMLElement,
    stylesheets: string[],
    viewportWidth = window.innerWidth || DEFAULT_VIEWPORT_WIDTH,
): CandidateVisibleText => {
    const cssBytes = stylesheets.reduce((size, css) => size + css.length, 0);
    if (original.querySelectorAll('*').length > MAX_MIRROR_ELEMENTS || cssBytes > MAX_VISIBILITY_CSS_BYTES) {
        return {
            text: '',
            warning: 'This email exceeds safe text-recovery limits. '
                + 'Its hidden and visible text could not be separated.',
        };
    }
    if (/jsdom/i.test(window.navigator.userAgent)) {
        return { text: readableCandidateText(original) };
    }
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-same-origin');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('data-candidate-visibility-mirror', '');
    frame.tabIndex = -1;
    frame.title = 'Temporary email visibility calculation';
    const width = Math.min(MAX_VIEWPORT_WIDTH, Math.max(1, viewportWidth));
    frame.style.cssText = `position:fixed;left:-100000px;top:0;width:${width}px;height:1000px;`
        + 'visibility:hidden;pointer-events:none;border:0;';
    try {
        document.body.appendChild(frame);
        const targetDocument = frame.contentDocument;
        const targetWindow = frame.contentWindow;
        if (!targetDocument || !targetWindow) throw new Error('Visibility mirror unavailable');
        const policy = targetDocument.createElement('meta');
        policy.setAttribute('http-equiv', 'Content-Security-Policy');
        policy.setAttribute('content',
            "default-src 'none'; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'");
        targetDocument.head.appendChild(policy);
        const mirrors = new Map<HTMLElement, HTMLElement>();
        const copyAttributes = (source: HTMLElement, target: HTMLElement): void => {
            Array.from(source.attributes).forEach(({ name, value }) => {
                if (SAFE_SELECTOR_ATTRIBUTE.test(name)) target.setAttribute(name, value);
            });
            const style = visibilityDeclarations(source.getAttribute('style') || '');
            if (style) target.setAttribute('style', style);
        };
        const copyElement = (source: HTMLElement): HTMLElement | null => {
            if (NON_CONTENT_TAGS.has(source.tagName)) return null;
            const mirror = targetDocument.createElement(source.localName);
            copyAttributes(source, mirror);
            mirrors.set(source, mirror);
            source.childNodes.forEach((child) => {
                if (child.nodeType === Node.ELEMENT_NODE) {
                    const copied = copyElement(child as HTMLElement);
                    if (copied) mirror.appendChild(copied);
                } else if (child.nodeType === Node.TEXT_NODE && child.textContent) {
                    mirror.appendChild(targetDocument.createTextNode('x'));
                }
            });
            return mirror;
        };
        const mirrorBody = copyElement(original);
        if (!mirrorBody) throw new Error('Visibility body unavailable');
        targetDocument.body.replaceWith(mirrorBody);
        if (original.ownerDocument.documentElement) {
            copyAttributes(original.ownerDocument.documentElement, targetDocument.documentElement);
        }
        [targetDocument.documentElement, mirrorBody].forEach((root, index) => {
            root.style.setProperty('display', index ? 'flow-root' : 'block', 'important');
            root.style.setProperty('visibility', 'visible', 'important');
            root.style.setProperty('opacity', '1', 'important');
            root.removeAttribute('hidden');
        });
        stylesheets.forEach((css) => {
            const sheet = targetDocument.createElement('style');
            sheet.textContent = sanitizeCandidateStylesheet(css, visibilityDeclarations);
            targetDocument.head.appendChild(sheet);
        });
        const text = readableCandidateText(original, (element) => {
            const mirror = mirrors.get(element);
            if (!mirror) return { skipSubtree: true, skipText: true };
            const computed = targetWindow.getComputedStyle(mirror);
            return {
                skipSubtree: computed.display === 'none' || computed.opacity === '0',
                skipText: computed.visibility === 'hidden' || computed.visibility === 'collapse',
            };
        });
        return { text };
    } catch {
        return {
            text: '',
            warning: 'The hidden and visible text in this email could not be safely separated for text recovery.',
        };
    } finally {
        frame.remove();
    }
};
